import { useState } from "react";
import {
  usePointsConfigurations,
  useApprovePointsConfiguration,
  useRejectPointsConfiguration,
} from "../hooks/usePointsConfig";
import type { PointsConfiguration } from "../services/pointsConfigApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";

const formatKes = (n: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(n);

/**
 * Checker view: Submitted points configurations awaiting approval. The server
 * enforces that the approver is not the submitter.
 */
export const PointsConfigApprovalsPage = () => {
  const notification = useNotification();
  const submittedQuery = usePointsConfigurations("Submitted");
  const approveMutation = useApprovePointsConfiguration();
  const rejectMutation = useRejectPointsConfiguration();

  const [rejectTarget, setRejectTarget] = useState<PointsConfiguration | null>(null);
  const [reason, setReason] = useState("");

  const approve = (c: PointsConfiguration) =>
    approveMutation
      .mutateAsync(c.id)
      .then(() => notification.success(`"${c.name}" approved and now active.`))
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Failed to approve configuration.");
      });

  const reject = () => {
    if (!rejectTarget) return;
    if (reason.trim().length < 10) {
      notification.error("Rejection reason must be at least 10 characters.");
      return;
    }
    rejectMutation
      .mutateAsync({ id: rejectTarget.id, rejectionReason: reason.trim() })
      .then(() => {
        notification.success("Configuration rejected.");
        setRejectTarget(null);
        setReason("");
      })
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Failed to reject configuration.");
      });
  };

  const configs = submittedQuery.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Points Configuration Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Configurations submitted for approval. {submittedQuery.isFetching && <span>Refreshing…</span>}
        </p>
      </div>

      {submittedQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : configs.length === 0 ? (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          Nothing awaiting approval.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Earn rate</TableHead>
              <TableHead>Point value</TableHead>
              <TableHead>Min redeem</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {configs.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.earnRatePoints} pt / {formatKes(c.earnRatePerAmount)}</TableCell>
                <TableCell>{formatKes(c.pointValueKes)}</TableCell>
                <TableCell>{c.minRedeemablePoints}</TableCell>
                <TableCell>
                  {c.conditions.length === 0 ? (
                    <span className="text-xs text-muted-foreground">All orders</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {c.conditions.map((cond) => (
                        <Badge key={cond.id} variant="outline" className="text-[10px]">
                          {cond.conditionType} {cond.operator} {cond.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" onClick={() => approve(c)} disabled={approveMutation.isPending}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejectTarget(c)}>
                    Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject configuration</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Reason (at least 10 characters)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={rejectMutation.isPending}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
